// ===================================================
// LeavePage.styles.js
// كل الـ styles الخاصة بـ LeavePage وكل sub-components
// ===================================================

// ─── Page wrapper ────────────────────────────────────────────────────────────
export const getPageStyle = ({ container, language }) => ({
  minHeight: "100vh",
  background: "linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)",
  padding: container.padding,
  direction: language === "ar" ? "rtl" : "ltr",
  fontFamily: "'Cairo', sans-serif",
});

// ─── Page header row ─────────────────────────────────────────────────────────
export const getHeaderStyle = ({ screenSize }) => ({
  display: "flex",
  alignItems: screenSize === "xs" ? "flex-start" : "center",
  justifyContent: "space-between",
  flexDirection: screenSize === "xs" ? "column" : "row",
  gap: screenSize === "xs" ? "16px" : "0",
  marginBottom: screenSize === "xs" ? "24px" : "32px",
  flexWrap: "wrap",
});

// ─── Page title ──────────────────────────────────────────────────────────────
export const getPageTitleStyle = ({ isDarkMode, screenSize, fonts }) => ({
  color: isDarkMode ? "#ffffff" : "#1a1a1a",
  fontSize: screenSize === "xs" ? fonts.title : "2rem",
  fontWeight: "800",
  margin: 0,
  fontFamily: "'Cairo', sans-serif",
  display: "flex",
  alignItems: "center",
  gap: "12px",
});

// ─── Page subtitle ───────────────────────────────────────────────────────────
export const getPageSubtitleStyle = ({ theme, fonts }) => ({
  color: theme.colors.subtext,
  fontSize: fonts.body,
  margin: "6px 0 0 0",
  fontFamily: "'Cairo', sans-serif",
});

// ─── Glass card (reused everywhere) ─────────────────────────────────────────
export const getCardStyle = ({ screenSize, spacing }) => ({
  background: "rgba(255,255,255,0.01)",
  backdropFilter: "blur(25px)",
  WebkitBackdropFilter: "blur(25px)",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: screenSize === "xs" ? "1rem" : "1.25rem",
  padding: spacing.padding,
  boxShadow: "0 0.5rem 2rem rgba(0,0,0,0.2)",
  marginBottom: spacing.margin,
  transition: "all 0.3s ease",
});

// ─── Primary button ──────────────────────────────────────────────────────────
export const getBtnPrimary = ({ screenSize, fonts, theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: screenSize === "xs" ? "10px 16px" : "12px 24px",
  borderRadius: "12px",
  border: "none",
  background: `linear-gradient(135deg, #2e7cba, #2e7cba)`,
  color: "#ffffff",
  cursor: "pointer",
  fontSize: screenSize === "xs" ? fonts.small : fonts.body,
  fontWeight: "700",
  fontFamily: "'Cairo', sans-serif",
  //boxShadow: `0 4px 16px ${theme.colors.primary}40`,
  transition: "all 0.3s ease",
  whiteSpace: "nowrap",
});


// ─── Tab button ──────────────────────────────────────────────────────────────
export const getTabButtonStyle = ({ tab, activeTab, screenSize, fonts, theme }) => ({
  padding: screenSize === "xs" ? "8px 14px" : "10px 20px",
  borderRadius: "999px",
  border: "none",
  cursor: "pointer",
  fontSize: screenSize === "xs" ? fonts.tiny : fonts.small,
  fontWeight: "600",
  fontFamily: "'Cairo', sans-serif",
  transition: "all 0.3s ease",
  background: activeTab === tab ? "#2e7cba" : "rgba(255,255,255,0.08)",
  color: activeTab === tab ? "#ffffff" : "rgba(255,255,255,0.7)",
  boxShadow: activeTab === tab ? `0 4px 12px #2e7cba40` : "none",
});

// ─── Toast notification ───────────────────────────────────────────────────────
export const getToastStyle = ({ type, language, fonts, theme }) => ({
  position: "fixed",
  top: "24px",
  right: language === "ar" ? "unset" : "24px",
  left: language === "ar" ? "24px" : "unset",
  zIndex: 9999,
  padding: "14px 20px",
  borderRadius: "12px",
  background:
    type === "success"
      ? `linear-gradient(135deg, ${theme.colors.success}, #059669)`
      : `linear-gradient(135deg, ${theme.colors.danger}, #dc2626)`,
  color: "#ffffff",
  fontSize: fonts.body,
  fontWeight: "600",
  fontFamily: "'Cairo', sans-serif",
  boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
  display: "flex",
  alignItems: "center",
  gap: "10px",
  maxWidth: "380px",
  animation: "slideIn 0.3s ease",
});

// ─── Holiday banner ───────────────────────────────────────────────────────────
export const getHolidayBannerStyle = ({ isDarkMode, screenSize, spacing, theme }) => ({
  ...getCardStyle({ isDarkMode, screenSize, spacing }),
  background: isDarkMode
    ? `linear-gradient(135deg, ${theme.colors.primary}15, ${theme.colors.secondary}15)`
    : `linear-gradient(135deg, ${theme.colors.primary}10, ${theme.colors.secondary}10)`,
  border: `1px solid ${theme.colors.primary}30`,
  display: "flex",
  alignItems: "center",
  gap: "16px",
  flexWrap: "wrap",
});

export const getHolidayBannerIconWrapper = ({ theme }) => ({
  width: "44px",
  height: "44px",
  borderRadius: "12px",
  background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.secondary})`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

export const getHolidayBannerTitle = ({ isDarkMode, fonts }) => ({
  color: isDarkMode ? "#ffffff" : "#1a1a1a",
  fontWeight: "700",
  fontSize: fonts.body,
  margin: 0,
  fontFamily: "'Cairo', sans-serif",
});

export const getHolidayBannerSubtitle = ({ theme, fonts }) => ({
  color: theme.colors.subtext,
  fontSize: fonts.small,
  margin: "2px 0 0 0",
  fontFamily: "'Cairo', sans-serif",
});

// ─── Stats grid ───────────────────────────────────────────────────────────────
export const getStatsGridStyle = ({ screenSize }) => ({
  display: "grid",
  gridTemplateColumns:
    screenSize === "xs" || screenSize === "sm" ? "repeat(2, 1fr)" : "repeat(4, 1fr)",
  gap: screenSize === "xs" ? "12px" : "20px",
  marginBottom: screenSize === "xs" ? "20px" : "28px",
});

export const getStatCardStyle = ({ isDarkMode, screenSize, spacing }) => ({
  ...getCardStyle({ isDarkMode, screenSize, spacing }),
  marginBottom: 0,
  padding: screenSize === "xs" ? "16px" : "24px",
  textAlign: "center",
});

export const getStatIconWrapper = ({ color }) => ({
  width: "44px",
  height: "44px",
  borderRadius: "12px",
  background: `${color}20`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto 12px",
});

export const getStatValueStyle = ({ color, screenSize }) => ({
  color,
  fontSize: screenSize === "xs" ? "1.5rem" : "2rem",
  fontWeight: "800",
  lineHeight: 1,
  fontFamily: "'Cairo', sans-serif",
});

export const getStatLabelStyle = ({ isDarkMode, fonts }) => ({
  color: isDarkMode ? "#ffffff" : "#1a1a1a",
  fontSize: fonts.small,
  fontWeight: "600",
  margin: "6px 0 4px",
  fontFamily: "'Cairo', sans-serif",
});

export const getStatTrendStyle = ({ theme, fonts }) => ({
  color: theme.colors.subtext,
  fontSize: fonts.tiny,
  fontFamily: "'Cairo', sans-serif",
});

// ─── Leave types grid (overview tab) ─────────────────────────────────────────
export const getLeaveTypesGridStyle = ({ screenSize }) => ({
  display: "grid",
  gridTemplateColumns:
    screenSize === "xs" || screenSize === "sm"
      ? "1fr"
      : screenSize === "md"
      ? "repeat(2, 1fr)"
      : "repeat(3, 1fr)",
  gap: screenSize === "xs" ? "16px" : "24px",
});

// ─── Recent history preview (overview tab) ───────────────────────────────────
export const getRecentHistoryCardStyle = ({ isDarkMode, screenSize, spacing }) => ({
  ...getCardStyle({ isDarkMode, screenSize, spacing }),
  marginTop: screenSize === "xs" ? "20px" : "28px",
});

export const getRecentHistoryHeaderStyle = () => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "20px",
});

export const getRecentHistoryTitleStyle = ({ isDarkMode, fonts }) => ({
  color: isDarkMode ? "#ffffff" : "#1a1a1a",
  fontSize: fonts.subtitle,
  fontWeight: "600",
  margin: 0,
  fontFamily: "'Cairo', sans-serif",
});

export const getViewAllBtnStyle = ({ theme, fonts }) => ({
  background: "none",
  border: "none",
  color: theme.colors.primary,
  cursor: "pointer",
  fontSize: fonts.small,
  fontWeight: "600",
  fontFamily: "'Cairo', sans-serif",
  display: "flex",
  alignItems: "center",
  gap: "4px",
});

// ─── Leave history row item ───────────────────────────────────────────────────
export const getLeaveRowStyle = ({ isDarkMode }) => ({
  display: "flex",
  alignItems: "center",
  gap: "16px",
  padding: "14px 16px",
  borderRadius: "12px",
  background: isDarkMode ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
  border: isDarkMode
    ? "1px solid rgba(255,255,255,0.06)"
    : "1px solid rgba(0,0,0,0.04)",
  cursor: "pointer",
  transition: "all 0.2s ease",
});

export const getLeaveRowHoverBg = ({ isDarkMode }) =>
  isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)";

export const getLeaveRowDefaultBg = ({ isDarkMode }) =>
  isDarkMode ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)";

export const getLeaveRowDotStyle = ({ color }) => ({
  width: "10px",
  height: "10px",
  borderRadius: "50%",
  background: color,
  flexShrink: 0,
});

export const getLeaveRowTitleStyle = ({ isDarkMode, fonts }) => ({
  color: isDarkMode ? "#ffffff" : "#1a1a1a",
  fontWeight: "600",
  fontSize: fonts.body,
  margin: 0,
  fontFamily: "'Cairo', sans-serif",
});

export const getLeaveRowDateStyle = ({ theme, fonts }) => ({
  color: theme.colors.subtext,
  fontSize: fonts.small,
  margin: "2px 0 0 0",
  fontFamily: "'Cairo', sans-serif",
});

export const getLeaveRowBadgeStyle = ({ color, fonts }) => ({
  padding: "4px 10px",
  borderRadius: "6px",
  background: `${color}18`,
  color,
  fontSize: fonts.tiny,
  fontWeight: "700",
  fontFamily: "'Cairo', sans-serif",
  whiteSpace: "nowrap",
});

// ─── Holidays grid (holidays tab) ────────────────────────────────────────────
export const getHolidaysGridStyle = ({ screenSize }) => ({
  display: "grid",
  gridTemplateColumns:
    screenSize === "xs" || screenSize === "sm"
      ? "1fr"
      : screenSize === "md"
      ? "repeat(2, 1fr)"
      : "repeat(3, 1fr)",
  gap: screenSize === "xs" ? "12px" : "20px",
});

// ─── Loading state ────────────────────────────────────────────────────────────
export const getLoadingCardStyle = ({ isDarkMode, screenSize, spacing }) => ({
  ...getCardStyle({ isDarkMode, screenSize, spacing }),
  textAlign: "center",
  padding: "48px 24px",
});

export const getLoadingTextStyle = ({ theme, fonts }) => ({
  color: theme.colors.subtext,
  fontSize: fonts.body,
  margin: 0,
  fontFamily: "'Cairo', sans-serif",
});

// ─── Leave type card ──────────────────────────────────────────────────────────
export const getLeaveTypeCardStyle = ({ isDarkMode, screenSize, theme }) => ({
  background: isDarkMode
    ? "rgba(255, 255, 255, 0.08)"
    : "rgba(255, 255, 255, 0.9)",
  backdropFilter: "blur(25px)",
  WebkitBackdropFilter: "blur(25px)",
  border: isDarkMode
    ? "2px solid rgba(255, 255, 255, 0.1)"
    : "2px solid rgba(0, 0, 0, 0.05)",
  borderRadius: screenSize === "xs" ? "16px" : "24px",
  padding: screenSize === "xs" ? "20px" : "28px",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  position: "relative",
  overflow: "hidden",
});

export const getLeaveTypeCardIconWrapperStyle = ({ color, screenSize }) => ({
  width: screenSize === "xs" ? "40px" : "48px",
  height: screenSize === "xs" ? "40px" : "48px",
  borderRadius: "12px",
  background: `${color}20`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "12px",
});

export const getLeaveTypeCardTitleStyle = ({ isDarkMode, fonts }) => ({
  color: isDarkMode ? "#ffffff" : "#1a1a1a",
  fontSize: fonts.body,
  fontWeight: "700",
  margin: "0 0 8px 0",
  fontFamily: "'Cairo', sans-serif",
});

export const getLeaveTypeCardProgressStyle = ({ isDarkMode }) => ({
  width: "100%",
  height: "6px",
  borderRadius: "3px",
  background: isDarkMode
    ? "rgba(255,255,255,0.1)"
    : "rgba(0,0,0,0.1)",
  overflow: "hidden",
  marginBottom: "12px",
});

export const getLeaveTypeCardProgressBarStyle = ({ color }) => ({
  height: "100%",
  background: color,
  transition: "width 0.3s ease",
  borderRadius: "3px",
});

export const getLeaveTypeCardStatsStyle = ({ isDarkMode, screenSize, fonts, theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: screenSize === "xs" ? "8px" : "12px",
});

export const getLeaveTypeCardStatItemStyle = ({ isDarkMode, fonts, theme }) => ({
  textAlign: "center",
});

export const getLeaveTypeCardStatLabelStyle = ({ isDarkMode, fonts, theme }) => ({
  color: theme.colors.subtext,
  fontSize: fonts.tiny,
  fontWeight: "600",
  margin: 0,
  fontFamily: "'Cairo', sans-serif",
});

export const getLeaveTypeCardStatValueStyle = ({ isDarkMode, fonts, color }) => ({
  color,
  fontSize: fonts.body,
  fontWeight: "800",
  margin: "4px 0 0 0",
  fontFamily: "'Cairo', sans-serif",
});

// ─── Leave type card decoration ───────────────────────────────────────────────
export const getLeaveTypeCardDecorationStyle = ({ screenSize, color }) => ({
  position: "absolute",
  top: "-30px",
  left: "-30px",
  width: screenSize === "xs" ? "60px" : "100px",
  height: screenSize === "xs" ? "60px" : "100px",
  borderRadius: "50%",
  background: `${color}20`,
  backdropFilter: "blur(40px)",
  opacity: 0.5,
});

// ─── Modal overlay ────────────────────────────────────────────────────────────
export const getModalOverlayStyle = ({ isDarkMode }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: isDarkMode
    ? "rgba(0, 0, 0, 0.7)"
    : "rgba(0, 0, 0, 0.5)",
  backdropFilter: "blur(4px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  padding: "20px",
});

export const getModalContentStyle = ({ isDarkMode, screenSize }) => ({
  background: isDarkMode
    ? "rgba(15, 23, 42, 0.98)"
    : "rgba(248, 250, 252, 0.98)",
  backdropFilter: "blur(25px)",
  WebkitBackdropFilter: "blur(25px)",
  border: isDarkMode
    ? "1px solid rgba(255, 255, 255, 0.1)"
    : "1px solid rgba(0, 0, 0, 0.05)",
  borderRadius: screenSize === "xs" ? "16px" : "24px",
  padding: screenSize === "xs" ? "24px" : "32px",
  maxWidth: "500px",
  width: "100%",
  maxHeight: "90vh",
  overflowY: "auto",
  boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
});

export const getModalCloseButtonStyle = ({ isDarkMode, theme }) => ({
  position: "absolute",
  top: "16px",
  right: "16px",
  background: "none",
  border: "none",
  color: isDarkMode ? "#ffffff" : "#1a1a1a",
  cursor: "pointer",
  fontSize: "24px",
  padding: "4px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

// ─── Form input field ─────────────────────────────────────────────────────────
export const getFormInputStyle = ({ isDarkMode, fonts }) => ({
  width: "100%",
  padding: "12px 16px",
  borderRadius: "8px",
  border: isDarkMode
    ? "1px solid rgba(255, 255, 255, 0.15)"
    : "1px solid rgba(0, 0, 0, 0.1)",
  background: isDarkMode
    ? "rgba(255, 255, 255, 0.05)"
    : "rgba(0, 0, 0, 0.02)",
  color: isDarkMode ? "#ffffff" : "#1a1a1a",
  fontSize: fonts.body,
  fontFamily: "'Cairo', sans-serif",
  transition: "all 0.2s ease",
  boxSizing: "border-box",
});

export const getFormInputFocusStyle = ({ isDarkMode, theme }) => ({
  borderColor: theme.colors.primary,
  background: isDarkMode
    ? "rgba(255, 255, 255, 0.08)"
    : "rgba(255, 255, 255, 0.5)",
  outline: "none",
  boxShadow: `0 0 0 2px ${theme.colors.primary}20`,
});

// ─── Form label ───────────────────────────────────────────────────────────────
export const getFormLabelStyle = ({ isDarkMode, fonts }) => ({
  color: isDarkMode ? "#ffffff" : "#1a1a1a",
  fontSize: fonts.small,
  fontWeight: "600",
  marginBottom: "8px",
  display: "block",
  fontFamily: "'Cairo', sans-serif",
});

// ─── Primary button in modal ───────────────────────────────────────────────────
export const getModalBtnPrimaryStyle = ({ screenSize, fonts, theme }) => ({
  padding: screenSize === "xs" ? "12px 20px" : "14px 28px",
  borderRadius: "8px",
  border: "none",
  background: `linear-gradient(135deg, #2e7cba, #2e7cba)`,
  color: "#ffffff",
  cursor: "pointer",
  fontSize: fonts.body,
  fontWeight: "700",
  fontFamily: "'Cairo', sans-serif",
  transition: "all 0.3s ease",
  width: "100%",
});

export const getModalBtnSecondaryStyle = ({ screenSize, fonts, isDarkMode }) => ({
  padding: screenSize === "xs" ? "12px 20px" : "14px 28px",
  borderRadius: "8px",
  border: isDarkMode
    ? "1px solid rgba(255, 255, 255, 0.2)"
    : "1px solid rgba(0, 0, 0, 0.1)",
  background: "transparent",
  color: isDarkMode ? "#ffffff" : "#1a1a1a",
  cursor: "pointer",
  fontSize: fonts.body,
  fontWeight: "700",
  fontFamily: "'Cairo', sans-serif",
  transition: "all 0.3s ease",
  width: "100%",
});

// ─── Header row with buttons ───────────────────────────────────────────────────
export const getHeaderButtonsContainerStyle = () => ({
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
});

// ─── Refresh button ───────────────────────────────────────────────────────────
export const getRefreshButtonStyle = ({ isDarkMode, theme, screenSize, fonts }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: screenSize === "xs" ? "10px 16px" : "12px 24px",
  borderRadius: "12px",
  border: isDarkMode
    ? "1px solid rgba(255, 255, 255, 0.15)"
    : "1px solid rgba(0, 0, 0, 0.1)",
  background: isDarkMode
    ? "rgba(255, 255, 255, 0.1)"
    : "rgba(0, 0, 0, 0.05)",
  color: isDarkMode ? "#ffffff" : "#1a1a1a",
  cursor: "pointer",
  fontSize: screenSize === "xs" ? fonts.small : fonts.body,
  fontWeight: "700",
  fontFamily: "'Cairo', sans-serif",
  transition: "all 0.3s ease",
  whiteSpace: "nowrap",
});

// ─── Tab bar container ────────────────────────────────────────────────────────
export const getTabBarStyle = ({ screenSize }) => ({
  display: "flex",
  gap: screenSize === "xs" ? "8px" : "12px",
  marginBottom: screenSize === "xs" ? "20px" : "28px",
  flexWrap: "wrap",
  overflowX: "auto",
  paddingBottom: "4px",
});

// ─── Recent leave history list ────────────────────────────────────────────────
export const getLeaveListContainerStyle = () => ({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
});

export const getLeaveListItemDotStyle = ({ isDarkMode, color }) => ({
  width: "8px",
  height: "8px",
  borderRadius: "50%",
  background: color,
  flexShrink: 0,
  marginTop: "6px",
});

export const getLeaveListItemTextWrapperStyle = () => ({
  flex: 1,
  minWidth: 0,
});

export const getLeaveListItemTitleStyle = ({ isDarkMode, fonts }) => ({
  color: isDarkMode ? "#ffffff" : "#1a1a1a",
  fontWeight: "600",
  fontSize: fonts.body,
  margin: 0,
  fontFamily: "'Cairo', sans-serif",
});

export const getLeaveListItemDateStyle = ({ theme, fonts }) => ({
  color: theme.colors.subtext,
  fontSize: fonts.small,
  margin: "2px 0 0 0",
  fontFamily: "'Cairo', sans-serif",
});

// ─── Global keyframes string (inject via <style> tag) ────────────────────────
export const globalKeyframes = `
  @keyframes slideIn {
    from { opacity: 0; transform: translateX(40px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes shine {
    from { left: -100%; }
    to   { left:  200%; }
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  ::-webkit-scrollbar       { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(128,128,128,0.3); border-radius: 2px; }
`;